<template>
  <div
    ref="yandexPayWrapper"
    class="container"
    :class="{ 'disabled': disabled }"
    @click.capture.stop="handleClick"
  />
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

import HTTP from 'axios';
import HTTPService from '@/services/Http';

import { API_URL, MERCHANT_ID } from '@/.env';

import { generateRandomString } from 'lib/utils/string';

export default {
  name: 'YandexPay',
  props: {
    merchantName: {
      type: String,
      required: true,
    },
    amount: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
    requestData: {
      type: Object,
      required: true,
    },
  },
  created() {
    this.onYaPayLoad();
  },
  beforeDestroy() {
    this.yandexPaymentButton.destroy();
  },
  data() {
    return {
      paymentToken: null,
      yandexPayment: null,
      yandexPaymentButton: null,
    };
  },
  computed: {
    getOrderId() {
      if (this.getPaymentId) {
        return this.getPaymentId;
      }

      return `order-${generateRandomString()}`;
    },
    getOrderAmount() {
      if (this.$validate.isEmptyString(this.amount)) {
        return '0';
      }

      return this.amount.replace(/\s/g, '');
    },
    getPaymentData() {
      console.log('env', process.env.NODE_ENV);

      return {
        // Окружение для работы (SandBox - тестовая среда, для продакшена нужно поменять на Production)
        env: process.env.NODE_ENV === 'development'
          ? YaPay.PaymentEnv.Sandbox : YaPay.PaymentEnv.Production, // eslint-disable-line no-undef
        // Версия Yandex Pay API
        version: 2,
        // Код страны и валюты с которыми работаем (код страны ни на что не влияет, а код валюты нет нужды выбирать из YaPay.CurrencyCode, можно передавать сразу из наших данных)
        countryCode: YaPay.CountryCode.Ru, // eslint-disable-line no-undef
        currencyCode: this.requestData.currency.code,
        // Объект данных продавца
        merchant: {
          id: MERCHANT_ID,
          name: this.merchantName,
          url: window.location.href,
        },
        // Объект данных товаров и суммы платежа
        order: {
          id: this.getOrderId,
          total: {
            amount: this.getOrderAmount,
          },
        },
        // Доступные методы оплаты
        paymentMethods: [
          {
            type: YaPay.PaymentMethodType.Card, // eslint-disable-line no-undef
            gateway: 'capusta-space',
            gatewayMerchantId: MERCHANT_ID,
            allowedAuthMethods: [YaPay.AllowedAuthMethod.PanOnly], // eslint-disable-line no-undef
            allowedCardNetworks: [
              YaPay.AllowedCardNetwork.Visa, // eslint-disable-line no-undef
              YaPay.AllowedCardNetwork.Mastercard, // eslint-disable-line no-undef
              YaPay.AllowedCardNetwork.Mir, // eslint-disable-line no-undef
              YaPay.AllowedCardNetwork.Maestro, // eslint-disable-line no-undef
              YaPay.AllowedCardNetwork.VisaElectron, // eslint-disable-line no-undef
            ],
          },
        ],
      };
    },
    ...mapGetters(['getPaymentId', 'getProcessToken']),
  },
  methods: {
    async onYaPayLoad() {
      console.log('LOAD: YaPay', this.getPaymentData);

      try {
        this.yandexPayment = await window.YaPay.createPayment(this.getPaymentData);

        if (this.$validate.isNull(this.yandexPayment)) {
          return;
        }

        this.addYandexPayButton();
      } catch (error) {
        this.yandexPayment = null;
        // logrocket.logger(error);
      }
    },
    addYandexPayButton() {
      console.log('Method: yandexPayment.createButton');

      this.yandexPaymentButton = this.yandexPayment.createButton({
        type: YaPay.ButtonType.Simple, // eslint-disable-line no-undef
        theme: YaPay.ButtonTheme.Black, // eslint-disable-line no-undef
        width: YaPay.ButtonWidth.Auto, // eslint-disable-line no-undef
      });

      this.yandexPaymentButton.mount(this.$refs.yandexPayWrapper);

      // eslint-disable-next-line no-undef
      this.yandexPayment.on(YaPay.PaymentEventType.Process, event => {
        // eslint-disable-next-line no-undef
        console.log('Event: Успешное завершение платежного виджета', YaPay.PaymentEventType.Process, event);
        this.paymentToken = event.token;

        // eslint-disable-next-line no-undef
        this.yandexPayment.complete(YaPay.CompleteReason.Success);
        this.processPayment();
      });

      // eslint-disable-next-line no-undef
      this.yandexPayment.on(YaPay.PaymentEventType.Error, event => {
        // eslint-disable-next-line no-undef
        console.log('Event: Закрытие платежного виджета с ошибкой:', YaPay.PaymentEventType.Error, event);
        // eslint-disable-next-line no-undef
        this.yandexPayment.complete(YaPay.CompleteReason.Error);

        this.$alert({
          type: 'danger',
          title: 'notification.yandex.payment.error',
        });
      });

      // eslint-disable-next-line no-undef
      this.yandexPayment.on(YaPay.PaymentEventType.Abort, event => {
        // eslint-disable-next-line no-undef
        console.log('Event: Закрытие платежного виджета пользователем', YaPay.PaymentEventType.Close, event);
        // eslint-disable-next-line no-undef
        this.yandexPayment.complete(YaPay.CompleteReason.Close);
      });

      // eslint-disable-next-line no-undef
      this.yandexPaymentButton.on(YaPay.ButtonEventType.Click, event => {
        // eslint-disable-next-line no-undef
        console.log('Event: Клик по кнопке платежного виджета', YaPay.ButtonEventType.Click, event);
        this.yandexPayment.checkout();
      });
    },
    handleClick() {
      if (this.disabled) {
        this.$emit('click-on-disabled');
        return;
      }

      this.paymentProcessing();
    },
    async paymentProcessing() {
      this.$emit('loading', true);

      await this.createPayment();

      if (!this.getPaymentId || !this.getProcessToken) {
        return;
      }

      await this.yandexPayment.update(this.getPaymentData);

      this.$emit('loading', false);
      this.yandexPaymentButton.onClick();
    },
    async createPayment() {
      console.log('handlePayment::createPayment', this.requestData);

      await HTTPService.createPayment(this.requestData)
        .then(response => {
          const { data } = response;
          console.log('CREATE_PAYMENT::RESPONSE', data);

          this.SET_TRANSACTION(data);
          this.SET_TRANSACTION_KEY(this.merchantName);

          console.log('getPaymentId', this.getPaymentId);
          console.log('getProcessToken', this.getProcessToken);
        })
        .catch(error => {
          console.log(error);
          // logrocket.logger(error);
        });
    },
    performTransaction() {
      return HTTP.post(
        `${API_URL}token/sale`,
        {
          paymentId: this.getPaymentId,
          tokenData: {
            token: this.paymentToken,
          },
          tokenType: 'yandexpay',
        }
      );
    },
    async processPayment() {
      this.$emit('loading', true);

      try {
        const { data } = await this.performTransaction();

        console.log('Method: processPayment', data);

        const { externalDigitalContentCode, enrolled } = data;
        console.log('last4Digits|enrolled', externalDigitalContentCode, enrolled);

        sessionStorage.setItem('passwordCard', externalDigitalContentCode);

        if (data.isCvvRequired) {
          this.$router.push({ name: 'confirmation.cvc' });
          return;
        }

        if (enrolled) {
          this.$router.push({
            name: 'ThreeDSecure',
            params: {
              data,
            },
          });
          return;
        }

        this.$router.push({ name: 'payment.origin' });
      } catch (error) {
        // logrocket.logger(error);
      }

      this.$emit('loading', false);
    },
    ...mapMutations(['SET_TRANSACTION', 'SET_TRANSACTION_KEY']),
  },
};
</script>

<style lang="sass" scoped>
  .container
    height: 50px

  ::v-deep.container > button
    margin: 0
    border: none

  .disabled
    background: transparent
    cursor: not-allowed

  ::v-deep.disabled > button
    pointer-events: none
    opacity: 0.5
</style>
